import React, { useState, useEffect, useMemo } from 'react';
import { useApp } from '../../context/AppContext';
import { useMuestrasPermissions } from '../../components/muestras/LayoutMuestras';
import { getTizados, createTizado, updateTizado, deleteTizado, getBases, getMuestrasBase, getMarcas, getTiposProducto, getEntalles, getTelas, getHilos, uploadTizadoFile } from '../../services/muestrasApi';
import ExcelGrid from '../../components/muestras/ExcelGrid';
import { toast } from 'sonner';
import { Scissors, Plus, Edit2, Trash2, X, Upload, Download, Eye, FileText, AlertTriangle, Search, RefreshCw } from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const MuestrasTizados = () => {
  const { user, token } = useApp();
  // Usar sistema de permisos granulares
  const { canCreate, canEdit, canDelete, isAdmin } = useMuestrasPermissions();
  
  // Permisos específicos para Tizados
  const puedeCrear = canCreate('tizados');
  const puedeEditar = canEdit('tizados');
  const puedeEliminar = canDelete('tizados');
  
  const getToken = () => token;
  const [data, setData] = useState([]);
  const [bases, setBases] = useState([]);
  const [muestrasBase, setMuestrasBase] = useState([]);
  const [catalogos, setCatalogos] = useState({ marcas: [], tipos: [], entalles: [], telas: [], hilos: [] });
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({ id_base: '', ancho: '', curva: '' });
  const [archivoFile, setArchivoFile] = useState(null);
  const [deleteItem, setDeleteItem] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [tizadosData, basesData, muestrasData, marcasData, tiposData, entallesData, telasData, hilosData] = await Promise.all([
        getTizados(), 
        getBases(),
        getMuestrasBase(),
        getMarcas(),
        getTiposProducto(),
        getEntalles(),
        getTelas(),
        getHilos()
      ]);
      setData(tizadosData || []);
      setBases(basesData || []);
      setMuestrasBase(muestrasData || []);
      setCatalogos({
        marcas: marcasData || [],
        tipos: tiposData || [],
        entalles: entallesData || [],
        telas: telasData || [],
        hilos: hilosData || []
      });
    } catch (error) {
      toast.error('Error cargando datos');
    } finally {
      setLoading(false);
    }
  };

  // Helper para obtener info de Muestra Base: id - marca - tipo - entalle - tela - hilo
  const getMuestraBaseLabel = (idBase) => {
    const base = bases.find(b => b.id_base === idBase);
    if (!base) return '-';
    
    const muestra = muestrasBase.find(m => m.id_muestra_base === base.id_muestra_base);
    if (!muestra) return `#${idBase} - Sin muestra base`;
    
    const marca = catalogos.marcas.find(x => x.id_marca === muestra.id_marca);
    const tipo = catalogos.tipos.find(x => x.id_tipo === muestra.id_tipo);
    const entalle = catalogos.entalles.find(x => x.id_entalle === muestra.id_entalle);
    const tela = catalogos.telas.find(x => x.id_tela === muestra.id_tela);
    const hilo = catalogos.hilos.find(x => x.id_hilo === muestra.id_hilo);
    
    const parts = [
      `#${idBase}`,
      marca?.nombre_marca,
      tipo?.nombre_tipo,
      entalle?.nombre,
      tela?.nombre,
      hilo?.codigo || hilo?.nombre
    ].filter(Boolean);
    
    return parts.join(' - ');
  };

  const openModal = (item = null) => {
    if (item) {
      setEditingItem(item);
      setFormData({ id_base: item.id_base || '', ancho: item.ancho || '', curva: item.curva || '' });
    } else {
      setEditingItem(null);
      setFormData({ id_base: '', ancho: '', curva: '' });
    }
    setArchivoFile(null);
    setShowModal(true);
  };

  const handleSubmit = async () => {
    if (!formData.id_base) { toast.error('La base es requerida'); return; }
    try {
      let itemId;
      if (editingItem) {
        await updateTizado(editingItem.id_tizado, formData);
        itemId = editingItem.id_tizado;
        toast.success('Tizado actualizado');
      } else {
        const result = await createTizado(formData);
        itemId = result.id_tizado;
        toast.success('Tizado creado');
      }
      if (archivoFile && itemId) {
        await uploadTizadoFile(itemId, archivoFile);
      }
      setShowModal(false);
      loadData();
    } catch (error) { toast.error(error.message); }
  };

  const confirmDelete = async () => {
    if (!deleteItem) return;
    setDeleting(true);
    try {
      await deleteTizado(deleteItem.id_tizado);
      toast.success('Tizado eliminado');
      setDeleteItem(null);
      loadData();
    } catch (error) { 
      toast.error(error.message); 
    } finally {
      setDeleting(false);
    }
  };

  const getFileUrl = (filename, forDownload = false) => {
    if (!filename) return null;
    if (filename.startsWith('http')) return filename;
    const base = `${API_BASE}/api/muestras/files/${filename}`;
    return forDownload ? `${base}?download=true` : base;
  };

  const getFileExtension = (filename) => {
    if (!filename) return '';
    return filename.split('.').pop().toUpperCase();
  };

  // Filtrado
  const filteredData = useMemo(() => {
    if (!searchQuery) return data;
    const q = searchQuery.toLowerCase();
    return data.filter(t => {
      const label = getMuestraBaseLabel(t.id_base).toLowerCase();
      return label.includes(q) || t.ancho?.toString().includes(q) || t.curva?.toLowerCase().includes(q);
    });
  }, [data, searchQuery, bases, muestrasBase, catalogos]);

  const columns = useMemo(() => [
    { accessorKey: 'id_tizado', header: 'ID', size: 60 },
    {
      accessorKey: 'muestra_base', header: 'MUESTRA BASE', size: 320,
      cell: ({ row }) => (
        <span className="font-medium text-gray-800 truncate block" title={getMuestraBaseLabel(row.original.id_base)}>
          {getMuestraBaseLabel(row.original.id_base)}
        </span>
      )
    },
    { accessorKey: 'ancho', header: 'ANCHO', size: 100, cell: ({ row }) => row.original.ancho ? `${row.original.ancho} cm` : '-' },
    { accessorKey: 'curva', header: 'CURVA', size: 150, cell: ({ row }) => row.original.curva || '-' },
    {
      accessorKey: 'archivo_tizado', header: 'ARCHIVO TIZADO', size: 150,
      cell: ({ row }) => {
        const hasFile = row.original.archivo_tizado;
        if (!hasFile) return <span className="text-gray-400 text-sm">Sin archivo</span>;
        const ext = getFileExtension(row.original.archivo_tizado);
        return (
          <div className="flex items-center gap-2">
            <span className={`px-2 py-1 rounded text-xs font-bold ${
              ext === 'PDF' ? 'bg-red-100 text-red-600' :
              ext === 'XLSX' || ext === 'XLS' ? 'bg-green-100 text-green-600' :
              'bg-blue-100 text-blue-600'
            }`}>{ext}</span>
            <a href={getFileUrl(row.original.archivo_tizado)} target="_blank" rel="noopener noreferrer" className="p-1 rounded bg-blue-50 text-blue-600 hover:bg-blue-100" title="Ver"><Eye className="w-4 h-4" /></a>
            <a href={getFileUrl(row.original.archivo_tizado, true)} target="_blank" rel="noopener noreferrer" className="p-1 rounded bg-green-50 text-green-600 hover:bg-green-100" title="Descargar"><Download className="w-4 h-4" /></a>
          </div>
        );
      }
    },
    {
      id: 'acciones', header: 'ACCIONES', size: 100,
      cell: ({ row }) => (
        <div className="flex items-center gap-1">
          {puedeEditar && <button onClick={() => openModal(row.original)} className="p-1.5 rounded bg-blue-50 text-blue-600 hover:bg-blue-100"><Edit2 className="w-4 h-4" /></button>}
          {puedeEliminar && <button onClick={() => setDeleteItem(row.original)} className="p-1.5 rounded bg-red-50 text-red-600 hover:bg-red-100" data-testid={`delete-tizado-${row.original.id_tizado}`}><Trash2 className="w-4 h-4" /></button>}
        </div>
      )
    }
  ], [bases, muestrasBase, catalogos, puedeEditar, puedeEliminar]);

  return (
    <div className="p-6 bg-gray-50 min-h-full" data-testid="muestras-tizados-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2.5 bg-purple-100 rounded-xl">
            <Scissors className="w-6 h-6 text-purple-600" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-800">Tizados</h1>
            <p className="text-gray-400 text-sm">Gestión de tizados</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={loadData} className="p-2 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200">
            <RefreshCw className="w-5 h-5" />
          </button>
          {puedeCrear && (
            <button onClick={() => openModal()} className="px-4 py-2.5 bg-purple-600 text-white rounded-xl hover:bg-purple-700 flex items-center gap-2 font-medium">
              <Plus className="w-4 h-4" />Agregar
            </button>
          )}
        </div>
      </div>

      {/* Buscador */}
      <div className="mb-4 relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Buscar por muestra base, ancho o curva..."
          className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 text-sm"
        />
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <ExcelGrid data={filteredData} columns={columns} loading={loading} pageSize={20} />
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-xl">
            <div className="px-6 py-4 border-b flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-800">{editingItem ? 'Editar Tizado' : 'Nuevo Tizado'}</h3>
              <button onClick={() => setShowModal(false)} className="p-1 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5 text-gray-500" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Base *</label>
                <select 
                  value={formData.id_base} 
                  onChange={(e) => setFormData({ ...formData, id_base: e.target.value })} 
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500"
                >
                  <option value="">Seleccionar base</option>
                  {bases.map(b => <option key={b.id_base} value={b.id_base}>{getMuestraBaseLabel(b.id_base)}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Ancho (cm)</label>
                  <input type="number" step="0.01" value={formData.ancho} onChange={(e) => setFormData({ ...formData, ancho: e.target.value })} className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500" placeholder="Ej: 150" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Curva</label>
                  <input type="text" value={formData.curva} onChange={(e) => setFormData({ ...formData, curva: e.target.value })} className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500" placeholder="Ej: S-M-L-XL" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Archivo Tizado</label>
                <div className="border border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-purple-400 cursor-pointer">
                  <input type="file" accept=".pdf,.xlsx,.xls,.dxf,.plt" onChange={(e) => setArchivoFile(e.target.files[0])} className="hidden" id="archivo-input" />
                  <label htmlFor="archivo-input" className="cursor-pointer">
                    <Upload className="w-5 h-5 text-gray-400 mx-auto mb-1" />
                    {archivoFile ? <p className="text-xs text-purple-600">{archivoFile.name}</p> : <p className="text-xs text-gray-500">Clic para seleccionar</p>}
                  </label>
                </div>
              </div>
            </div>
            <div className="px-6 py-4 border-t bg-gray-50 flex justify-end gap-3 rounded-b-2xl">
              <button onClick={() => setShowModal(false)} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-xl font-medium">Cancelar</button>
              <button onClick={handleSubmit} className="px-4 py-2 bg-purple-600 text-white rounded-xl hover:bg-purple-700 font-medium">{editingItem ? 'Guardar' : 'Crear'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Confirmación de Eliminación */}
      {deleteItem && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl w-full max-w-md mx-4 shadow-xl">
            <div className="p-6 text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertTriangle className="w-8 h-8 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">¿Eliminar Tizado?</h3>
              <p className="text-gray-500 mb-6">
                ¿Estás seguro de eliminar el tizado <strong>#{deleteItem.id_tizado}</strong>? Esta acción no se puede deshacer.
              </p>
              <div className="flex justify-center gap-3">
                <button 
                  onClick={() => setDeleteItem(null)} 
                  className="px-6 py-2.5 text-gray-600 hover:bg-gray-100 rounded-xl font-medium"
                  disabled={deleting}
                >
                  Cancelar
                </button>
                <button 
                  onClick={confirmDelete} 
                  className="px-6 py-2.5 bg-red-600 text-white rounded-xl hover:bg-red-700 font-medium disabled:opacity-50"
                  disabled={deleting}
                  data-testid="confirm-delete-btn"
                >
                  {deleting ? 'Eliminando...' : 'Eliminar'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MuestrasTizados;
